/**
 * ONE DEVICE, EVERYTHING ABOUT IT — the Fleet Monitoring device page.
 *
 * Approved mockup: `docs/mockups/fleet-monitoring-redesign-mockup.html`,
 * screen "2 · Device page". Clicking a till in the list opens THIS page — no
 * side drawer. The header answers "what is it and is it OK" (photo, name,
 * where it is, online light, copyable ids) and carries the three things
 * support does most (Collect logs, Restart, Screenshot). The at-a-glance strip
 * answers "is an update coming, is anything stuck, when did it last talk to
 * us". Tabs hold the rest; destructive actions live alone in Danger zone.
 *
 * CHROME: black bar + ruby gradient underline — `InventoryChrome`, the
 * canonical super-admin header. This page now lives under Fleet Monitoring,
 * not as a child of Device Management, so the old emerald pills are gone.
 *
 * ONE COMMAND READ: the page owns `useDeviceCommands` for the header's
 * actions and the Logs badge, and hands the list to Overview. A capture the
 * header requests is passed to the Logs tab as `freshCommand`, so it shows
 * "Waiting for till" immediately.
 *
 * WHAT THIS PAGE DOES NOT DO: the hardware life of the device — order,
 * allocation, provisioning, condition, journey — belongs to the inventory
 * unit page (rendered in the Unit tab). Two timelines over one device drift.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Camera, ChevronLeft, Copy, FileText, Monitor, MoreHorizontal, RefreshCw, RotateCw,
} from 'lucide-react';
import type { DeviceHealthProblem, FleetActionId, FleetCommand } from '@platform/shared-types';
import { useFleetDevice, DEFAULT_DEVICE_TYPE } from './useFleetDevice';
import {
  DEVICE_TABS, toDeviceTabId, deviceTitle, isOnline, relativeTime, modelImage,
  type DeviceTabId, type DeviceTabProps,
} from './deviceDetail';
import OverviewTab from './OverviewTab';
import UnitTab from './UnitTab';
import HealthTab from './HealthTab';
import HistoryTab from './HistoryTab';
import LogsTab from '../../fleet/device/LogsTab';
import IssuesTab from '../../fleet/device/IssuesTab';
import UpdatesTab from '../../fleet/device/UpdatesTab';
import DangerTab from '../../fleet/device/DangerTab';
import { useCanManageFleet } from '../../fleet/device/useCanManageFleet';
import { copyText, isCapture } from '../../fleet/device/logcat';
import {
  TONE_PILL, isOpenIssue, updateStatus, uploadsWaiting,
} from '../../fleet/fleetStatus';
import type { FleetRow } from '../fleet/fleetRows';
import { useDeviceCommands } from '../fleet/useDeviceCommands';
import TierTwoConfirm from '../fleet/TierTwoConfirm';
import { humaniseCommandError } from '../fleet/FleetDeviceDrawer';
import { FLEET_UI_ACTIONS } from '../fleet/actionRegistry';
import { deviceHealthApi } from '../../../services/deviceHealthApi';
import { toastService } from '../../../services/toastService';
import { api } from '../../../services/apiService';
import ShimmerImage from '../../../components/ShimmerImage';
import { RubyLoader } from '../../../components/RubyLoader';
import { RubyGemMark } from '../../../components/RubyGemMark';

const TAB_BODY: Record<DeviceTabId, React.FC<DeviceTabProps>> = {
  overview: OverviewTab,
  logs: LogsTab,
  issues: IssuesTab,
  health: HealthTab,
  updates: UpdatesTab,
  history: HistoryTab,
  unit: UnitTab,
  danger: DangerTab,
};

const ISSUES_POLL_MS = 60_000;

const RUBY_UNDERLINE = { background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' };

const ACTION_LABEL: Record<string, string> = Object.fromEntries(FLEET_UI_ACTIONS.map((a) => [a.id, a.label]));

/** `terminalControl` is not on `FleetRow` yet — read defensively, as ActionTiers does. */
function isLocked(row: FleetRow): boolean {
  const tc = (row.device as unknown as { terminalControl?: { status?: string } } | undefined)?.terminalControl;
  return tc?.status === 'locked';
}

export default function FleetDevicePage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const [params, setParams] = useSearchParams();
  const location = useLocation();

  // `?type=` so a second device type can be linked here without a new route.
  const deviceType = params.get('type') || DEFAULT_DEVICE_TYPE;
  const tab = toDeviceTabId(params.get('tab'));

  const { row, model, loading, error, notFound, refresh } = useFleetDevice(deviceId, deviceType);
  const cmds = useDeviceCommands(deviceId, deviceType);
  const canManage = useCanManageFleet();

  const [openIssues, setOpenIssues] = useState<DeviceHealthProblem[] | null | undefined>(undefined);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [fresh, setFresh] = useState<FleetCommand | null>(null);
  const [busy, setBusy] = useState<FleetActionId | null>(null);
  const [tierTwo, setTierTwo] = useState<FleetActionId | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const setTab = useCallback((next: DeviceTabId) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: false });
  }, [setParams]);

  // Open Issue Console problems for this till — the Issues badge and
  // Overview's card read this one list.
  useEffect(() => {
    if (!deviceId) return;
    let live = true;
    const read = () => deviceHealthApi.problems({ deviceId, status: 'open', window: '7d' })
      .then((list) => { if (live) setOpenIssues(list || []); })
      .catch(() => { if (live) setOpenIssues((prev) => (prev === undefined ? null : prev)); });
    void read();
    const t = window.setInterval(() => { void read(); }, ISSUES_POLL_MS);
    return () => { live = false; window.clearInterval(t); };
  }, [deviceId]);

  // The business name for the "where" line. A failed read leaves it out —
  // never a raw tenant id where a name belongs.
  const tenantId = row?.tenantId;
  useEffect(() => {
    if (!tenantId) { setTenantName(null); return; }
    let live = true;
    api.superAdmin.getTenant(tenantId)
      .then((t: any) => { if (live) setTenantName(t?.name || t?.businessName || null); })
      .catch(() => { if (live) setTenantName(null); });
    return () => { live = false; };
  }, [tenantId]);

  const onFleetMonitoring = location.pathname.startsWith('/fleet-monitoring');
  const backToFleet = onFleetMonitoring ? '/fleet-monitoring' : '/pos-config?tab=fleet';
  const backLabel = onFleetMonitoring ? 'Fleet Monitoring' : 'Fleet';

  const run = async (action: FleetActionId, params?: Record<string, unknown>) => {
    if (!row?.deviceId || !row.deviceType) {
      setActionError('This device has no live check-in to send a command to.');
      return;
    }
    setBusy(action);
    setActionError(null);
    try {
      // Read the SERVER'S state back, never assume success.
      const created = await cmds.issue(action, params ? { params } : undefined);
      const offline = created.state === 'queued' && !created.sentAt;
      const label = ACTION_LABEL[action] || action.replace(/_/g, ' ');
      if (isCapture(created)) {
        setFresh(created);
        setTab('logs');
        toastService.success(offline ? `${label} queued — the till is offline` : `${label} requested — it appears under Logs in a few seconds`);
      } else {
        toastService.success(offline ? `${label} queued · device offline` : `${label} sent`);
      }
      refresh();
    } catch (e: any) {
      setActionError(humaniseCommandError(e?.message));
    } finally {
      setBusy(null);
    }
  };

  // ── states that are not the page ──────────────────────────────────────────
  if (loading && !row) {
    return <Shell back={backToFleet} backLabel={backLabel} title="Loading…"><RubyLoader variant="page" label="Reading this device…" /></Shell>;
  }
  if (notFound) {
    // "We looked and it is not there" — deliberately not the same words as a failed read.
    return (
      <Shell back={backToFleet} backLabel={backLabel} title="Device not found">
        <div className="p-8">
          <p className="text-sm text-stone-600">
            No device checked in as <span className="font-mono text-stone-900">{deviceId}</span>.
          </p>
          <Link to={backToFleet} className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <ChevronLeft size={16} /> Back to {backLabel}
          </Link>
        </div>
      </Shell>
    );
  }
  if (error && !row) {
    return (
      <Shell back={backToFleet} backLabel={backLabel} title="Could not load">
        <div className="p-8">
          <p className="text-sm text-rose-700">{error}</p>
          <button onClick={refresh} className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 text-sm font-semibold text-white hover:bg-[#9F1239]">
            <RefreshCw size={16} /> Try again
          </button>
        </div>
      </Shell>
    );
  }
  if (!row) return <Shell back={backToFleet} backLabel={backLabel} title="Device"><div className="p-8 text-sm text-stone-400">Nothing to show.</div></Shell>;

  const Body = TAB_BODY[tab];
  const online = isOnline(row);
  const seen = relativeTime(row.updatedAt);
  const title = deviceTitle(row, model);
  const where = [tenantName, row.locationName, row.registerName ? `Register ${row.registerName}` : null].filter(Boolean).join(' · ');
  const modelName = (model as { modelName?: string } | null)?.modelName || [row.hardwareBrand, row.hardwareModel].filter(Boolean).join(' ') || null;
  const captureCount = cmds.commands.filter(isCapture).length;
  const openCount = (openIssues || []).filter(isOpenIssue).length;

  const ids: [string, string | null | undefined][] = [
    ['Model', modelName],
    ['Serial', row.serialNumber],
    ['Tracking', row.trackingId],
    ['Provisioning', row.provisionCode],
  ];

  return (
    <div className="-m-3 min-h-screen bg-stone-100 md:-m-6">
      <header className="bg-black px-4 pt-4 text-white md:px-6">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-stone-400">
          {onFleetMonitoring ? (
            <>
              <span>Deployment &amp; Release</span><span aria-hidden="true">›</span>
              <Link to={backToFleet} className="hover:text-white">Fleet Monitoring</Link>
            </>
          ) : (
            <>
              <Link to="/pos-config" className="hover:text-white">Device Management</Link><span aria-hidden="true">›</span>
              <Link to={backToFleet} className="hover:text-white">Fleet</Link>
            </>
          )}
          <span aria-hidden="true">›</span>
          <span className="text-stone-300">{title}</span>
        </nav>

        <div className="flex flex-wrap items-start gap-5 pb-4 pt-3">
          <ShimmerImage
            src={modelImage(model)}
            alt={modelName || title}
            width={132}
            height={132}
            objectFit="contain"
            variant="dark"
            className="flex-none rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-800 to-zinc-950"
            fallbackBgColor="bg-zinc-900"
            fallbackIcon={<Monitor size={52} className="text-stone-500" />}
          />

          <div className="grid min-w-[240px] flex-1 gap-1.5">
            {/* The live light and the release status read the same timestamp
                (ONLINE_WITHIN_MS), so they can never disagree on one device.
                Green here means "online" — a success state. */}
            <span className={`inline-flex w-max items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              online ? 'bg-green-950 text-green-300' : 'bg-zinc-800 text-stone-300'
            }`}>
              <i className={`h-2 w-2 rounded-full ${online ? 'bg-green-500' : 'bg-stone-500'}`} />
              {online ? `Online · checked in ${seen || 'just now'}` : `Not reporting${seen ? ` · last seen ${seen}` : ''}`}
            </span>
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {where && <p className="text-[13.5px] text-stone-400">{where}</p>}
            <div className="mt-1 flex flex-wrap gap-1.5">
              {ids.filter(([, v]) => !!v).map(([k, v]) => <IdChip key={k} label={k} value={v!} />)}
            </div>
          </div>

          {canManage && row.deviceId && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => run('collect_logs')}
                disabled={!!busy}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 text-[13.5px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-60"
              >
                {busy === 'collect_logs' ? <RubyGemMark size={16} drawing color="currentColor" /> : <FileText size={16} />} Collect logs
              </button>
              <button
                type="button"
                onClick={() => setTierTwo('restart')}
                disabled={!!busy}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-stone-700 px-4 text-[13.5px] font-semibold text-stone-100 hover:bg-zinc-800 disabled:opacity-60"
              >
                {busy === 'restart' ? <RubyGemMark size={16} drawing color="currentColor" /> : <RotateCw size={16} />} Restart
              </button>
              <button
                type="button"
                onClick={() => run('capture_screen')}
                disabled={!!busy}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-stone-700 px-4 text-[13.5px] font-semibold text-stone-100 hover:bg-zinc-800 disabled:opacity-60"
              >
                {busy === 'capture_screen' ? <RubyGemMark size={16} drawing color="currentColor" /> : <Camera size={16} />} Screenshot
              </button>
              <MoreMenu row={row} busy={busy} onRun={(a) => run(a)} onTierTwo={setTierTwo} />
            </div>
          )}
        </div>

        <div role="tablist" aria-label="Device sections" className="-mb-px flex gap-1 overflow-x-auto">
          {DEVICE_TABS.map((t) => {
            const on = tab === t.id;
            const danger = t.id === 'danger';
            const badge = t.id === 'logs' ? captureCount : t.id === 'issues' ? openCount : 0;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setTab(t.id)}
                className={`inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap border-b-2 px-3.5 text-[13.5px] font-medium ${
                  danger ? 'ml-auto text-rose-300' : on ? 'text-white' : 'text-stone-400 hover:text-white'
                } ${on ? (danger ? 'border-rose-300' : 'border-[#DC143C]') : 'border-transparent'}`}
              >
                {t.label}
                {badge > 0 && (
                  <span className="rounded-full bg-[#DC143C] px-1.5 text-[11px] font-semibold text-white tabular-nums">{badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </header>
      <div style={RUBY_UNDERLINE} />

      <main className="mx-auto grid w-full max-w-[1440px] gap-4 p-4 text-stone-900 md:p-6">
        {/* A degraded refresh says so without blanking what is already on screen. */}
        {error && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
            {error} — showing the last good read.
          </p>
        )}
        {actionError && (
          <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-[12.5px] font-semibold text-rose-700">
            {actionError}
          </p>
        )}
        <Glance row={row} />

        <Body
          row={row}
          model={model}
          onRefresh={refresh}
          goTo={setTab}
          freshCommand={fresh}
          commands={cmds.commands}
          openIssues={openIssues}
        />
      </main>

      {tierTwo && (
        <TierTwoConfirm
          row={row}
          action={tierTwo}
          busy={busy === tierTwo}
          onCancel={() => setTierTwo(null)}
          onConfirm={(p) => {
            const action = tierTwo;
            setTierTwo(null);
            void run(action, p);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header pieces
// ---------------------------------------------------------------------------

function IdChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 py-0.5 pl-2 pr-0.5 text-xs text-stone-300">
      <b className="font-medium text-stone-500">{label}</b>
      <span className="font-mono">{value}</span>
      <button
        type="button"
        aria-label={`Copy ${label}`}
        // Never log the value — the provisioning code is a pairing credential.
        onClick={async () => {
          const ok = await copyText(value);
          if (ok) toastService.success(`${label} copied`);
          else toastService.error('Could not copy — copy it by hand');
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 hover:bg-zinc-800 hover:text-white"
      >
        <Copy size={13} />
      </button>
    </span>
  );
}

/**
 * The less frequent tier-1/tier-2 actions the old Support tab carried — kept
 * reachable from the header so nothing was lost when that tab went away.
 * Custom popover (never a native select), portalled so no ancestor clips it.
 */
function MoreMenu({
  row, busy, onRun, onTierTwo,
}: { row: FleetRow; busy: FleetActionId | null; onRun: (a: FleetActionId) => void; onTierTwo: (a: FleetActionId) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const W = 240;

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const b = btnRef.current?.getBoundingClientRect();
    if (b) setPos({ top: b.bottom + 4, left: Math.max(8, Math.min(b.right - W, window.innerWidth - W - 8)) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const locked = isLocked(row);
  const items: { key: string; label: string; help: string; onClick: () => void }[] = [
    { key: 'device_report', label: 'Device report', help: 'Peripherals, USB bus, displays, storage', onClick: () => onRun('device_report') },
    { key: 'force_resync', label: 'Force re-sync', help: 'Drain the sale outbox and re-pull config', onClick: () => onRun('force_resync') },
    locked
      ? { key: 'unlock', label: 'Unlock till', help: 'Clear the freeze', onClick: () => onTierTwo('unlock') }
      : { key: 'lock', label: 'Lock till', help: 'Freeze the screen with a message', onClick: () => onTierTwo('lock') },
    { key: 'apply_policy', label: 'Apply lockdown policy', help: 'ADB off · OEM unlock off · safe boot off', onClick: () => onTierTwo('apply_policy') },
  ];

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={!!busy}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-stone-700 text-stone-200 hover:bg-zinc-800 disabled:opacity-60"
      >
        <MoreHorizontal size={18} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="More actions"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: W }}
          className="z-[60] rounded-xl border border-stone-200 bg-white p-1 text-stone-900 shadow-lg"
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); it.onClick(); }}
              className="grid min-h-[44px] w-full rounded-lg px-3 py-1.5 text-left hover:bg-stone-50"
            >
              <span className="text-[13px] font-semibold">{it.label}</span>
              <span className="text-[11.5px] text-stone-500">{it.help}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// At a glance
// ---------------------------------------------------------------------------

function Fact({ label, children, sub }: { label: string; children: React.ReactNode; sub: React.ReactNode }) {
  return (
    <div className="grid min-w-0 content-start gap-1 border-b border-stone-200 px-4 py-3.5 last:border-b-0 sm:border-r lg:border-b-0 lg:last:border-r-0">
      <span className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">{label}</span>
      <span className="min-w-0 break-words text-[17px] font-semibold">{children}</span>
      <span className="text-[12.5px] text-stone-500">{sub}</span>
    </div>
  );
}

function Glance({ row }: { row: FleetRow }) {
  const running = row.appVersion || row.displayVersion;
  const status = updateStatus(row);
  const up = uploadsWaiting(row);
  const oldest = up?.oldestAt ? relativeTime(up.oldestAt) : null;
  const seen = relativeTime(row.updatedAt);

  return (
    <section aria-label="At a glance" className="grid overflow-hidden rounded-2xl border border-stone-200 bg-white sm:grid-cols-2 lg:grid-cols-[repeat(5,minmax(0,1fr))]">
      <Fact
        label="Running"
        sub={!row.targetVersion ? 'No target set' : row.drift ? <>Target <span className="font-mono">{row.targetVersion}</span></> : 'On target'}
      >
        <span className="font-mono">{running || '—'}</span>
      </Fact>
      <Fact label="Update" sub={status.detail}>
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${TONE_PILL[status.tone]}`}>{status.label}</span>
      </Fact>
      <Fact
        label="Uploads waiting"
        sub={up === null ? 'Not reported by this build' : up.total === 0 ? 'Sales, receipts and punches all sent' : `${oldest ? `Oldest from ${oldest}` : 'Waiting to send'}${up.sales && up.other ? ` · ${up.other} other` : ''}`}
      >
        {up === null ? <span className="text-stone-400">—</span>
          : up.total === 0 ? 'None'
            : (
              <span className={isOnline(row) ? 'text-amber-700' : 'text-[#B91C1C]'}>
                {up.sales ? `${up.sales} sale${up.sales === 1 ? '' : 's'}` : `${up.other} other upload${up.other === 1 ? '' : 's'}`}
              </span>
            )}
      </Fact>
      <Fact label="Last check-in" sub="Checks in every 60s while open">{seen || '—'}</Fact>
      <Fact
        label="Follows"
        sub={`Channel ${row.assignedChannel || '—'} · auto-update ${row.autoUpdate ? 'on' : 'off'}`}
      >
        {row.pinnedVersion ? <>Pinned · <span className="font-mono text-[15px]">{row.pinnedVersion}</span></> : `${row.releaseRing[0]?.toUpperCase() ?? ''}${row.releaseRing.slice(1)} ring`}
      </Fact>
    </section>
  );
}

/** The black bar on its own, for the states that have no device to describe. */
function Shell({ title, back, backLabel, children }: { title: string; back: string; backLabel: string; children: React.ReactNode }) {
  return (
    <div className="-m-3 min-h-screen bg-stone-100 md:-m-6">
      <header className="bg-black px-4 py-5 text-white md:px-6">
        <Link to={back} className="inline-flex min-h-[44px] items-center text-xs text-stone-400 hover:text-white">‹ {backLabel}</Link>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">{title}</h1>
      </header>
      <div style={RUBY_UNDERLINE} />
      <main className="text-stone-900">{children}</main>
    </div>
  );
}
